import {
  TextField,
  FormControlLabel,
  Box,
  Typography,
  Alert,
  Checkbox,
  Stack,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { IntakeStatusNote } from '@shared/patient-intake-status';

import { useAuth } from '../../../../hooks/index.js';
import { getUserTypeValidity, USER_TYPES } from '../../../../utils/index.js';
import { NoteDisplay } from '../components';
import type { CompleteStepFormData } from './CompleteStepDialog.tsx';
import { usePatientIntakeStatusContext } from '../hooks/usePatientIntakeStatusContext.ts';

type NoteFormSectionProps = {
  currentNote: Pick<IntakeStatusNote, 'id' | 'visibility'> | null;
  stepId: string | null;
};

export const NoteFormSection: React.FC<NoteFormSectionProps> = ({ currentNote, stepId }) => {
  const { userType } = useAuth();
  const { patientId } = usePatientIntakeStatusContext();
  const {
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useFormContext<CompleteStepFormData>();

  const notes = watch('notes');
  const keepCurrentNote = watch('keepCurrentNote');
  const isMedicalNote = watch('isMedicalNote');
  const isNoteVisibleToPatient = watch('isNoteVisibleToPatient');

  const canUserAddMedicalNote = getUserTypeValidity({
    userType,
    validUsers: [USER_TYPES.DIRECTOR, USER_TYPES.NURSE, USER_TYPES.DOCTOR],
  });

  const shouldHideNoteVisibilityOptions = !notes || keepCurrentNote || isSubmitting;

  return (
    <Stack direction="column" gap={1}>
      <FormControl error={!!errors.notes}>
        <FormLabel component="legend">Would you like to add any notes?</FormLabel>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              multiline
              rows={3}
              disabled={keepCurrentNote || isSubmitting}
              placeholder="Add any relevant notes about this step (optional)"
              error={!!errors.notes}
              helperText={errors.notes?.message}
            />
          )}
        />
        <FormHelperText>
          Updating this note will replace the current note shown. All previous notes will remain visible in the
          step&apos;s history.
        </FormHelperText>
      </FormControl>

      <Box sx={{ display: !currentNote || !!notes.trim() ? 'none' : 'block' }}>
        <Controller
          name="keepCurrentNote"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              control={
                <Checkbox checked={value} onChange={onChange} disabled={!currentNote || !!notes || isSubmitting} />
              }
              label="Keep previous notes"
            />
          )}
        />

        {!!patientId && !!currentNote && <NoteDisplay noteConfig={currentNote} stepId={stepId ?? null} />}
      </Box>

      <Box sx={{ display: shouldHideNoteVisibilityOptions ? 'none' : 'block' }}>
        {canUserAddMedicalNote && (
          <Controller
            name="isMedicalNote"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Checkbox checked={value} onChange={onChange} disabled={!notes || keepCurrentNote || isSubmitting} />
                }
                label="This is a medical note"
              />
            )}
          />
        )}

        {(!notes || keepCurrentNote) && (
          <Typography variant="caption" sx={{ display: 'block', ml: 4 }}>
            {keepCurrentNote
              ? 'Cannot modify note type when keeping current note'
              : 'Add a note above to enable this option'}
          </Typography>
        )}

        {isMedicalNote && !keepCurrentNote && (
          <Alert severity="warning">
            This note will only be visible to nurses
            {isNoteVisibleToPatient ? ', doctors, and the patient' : ' and doctors'}.
          </Alert>
        )}

        {errors.isMedicalNote && (
          <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4 }}>
            {errors.isMedicalNote.message}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: shouldHideNoteVisibilityOptions ? 'none' : 'block' }}>
        <Controller
          name="isNoteVisibleToPatient"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              control={
                <Checkbox checked={value} onChange={onChange} disabled={!notes || keepCurrentNote || isSubmitting} />
              }
              label="Make this note visible to the patient"
            />
          )}
        />

        {(!notes || keepCurrentNote) && (
          <Typography variant="caption" sx={{ display: 'block', ml: 4 }}>
            {keepCurrentNote
              ? 'Cannot modify visibility when keeping current note'
              : 'Add a note above to enable this option'}
          </Typography>
        )}

        {isNoteVisibleToPatient && !keepCurrentNote && (
          <Alert severity="warning">This note will be visible to the patient in the intake process.</Alert>
        )}

        {errors.isNoteVisibleToPatient && (
          <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4 }}>
            {errors.isNoteVisibleToPatient.message}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
